// app/javascript/controllers/dae20/component_loader_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["overlay", "content"]
  static values = { lazyUrl: String }

  connect() {
    // bind'ы чтобы корректно снимать listeners в disconnect
    this._beforeCache = this.beforeCache.bind(this)
    this._onTurboLoad = this.onTurboLoad.bind(this)
    this._onFrameLoad = this.onFrameLoad.bind(this)
    this._onBeforeStreamRender = this.onBeforeStreamRender.bind(this)
    this._onAfterStreamRender = this.onAfterStreamRender.bind(this)

    // Debounce для пачки turbo-stream (replace компонента + очистка modal и т.п.)
    this._scheduleHide = this.scheduleHide.bind(this)

    // Таймеры:
    // - _hideDebounceTimeout: скрыть лоадер после серии stream-ов
    // - _maxVisibleTimeout: аварийно скрыть если что-то пошло не так
    this._hideDebounceTimeout = null
    this._maxVisibleTimeout = null

    // На обычном заходе компонент уже отрендерен сервером.
    // Поэтому лоадер должен быть скрыт (если вдруг в DOM он оказался видимым).
    this.hideLoader({ keepTimers: false })

    // 3) На turbo-back ключевое: перед уходом в snapshot
    document.addEventListener("turbo:before-cache", this._beforeCache)

    // 3) Прячем только когда Turbo ЗАКОНЧИЛ визит и отрендерил финальную версию
    // (важно: НЕ turbo:render, иначе на snapshot-restore можно спрятать слишком рано)
    document.addEventListener("turbo:load", this._onTurboLoad)
    document.addEventListener("turbo:frame-load", this._onFrameLoad)

    // Важно: при turbo-stream replace (например, после сохранения в модалке)
    // события turbo:load/turbo:frame-load НЕ срабатывают.
    // Поэтому ловим события stream-render и показываем/прячем оверлей вокруг replace текущего компонента.
    document.addEventListener("turbo:before-stream-render", this._onBeforeStreamRender)
    document.addEventListener("turbo:after-stream-render", this._onAfterStreamRender)

    // Lazy-load: если компонент помечен как lazy — загружаем по AJAX
    if (this.hasLazyUrlValue && this.lazyUrlValue) {
      this.showLoader()
      this._loadLazy()
    }
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this._beforeCache)
    document.removeEventListener("turbo:load", this._onTurboLoad)
    document.removeEventListener("turbo:frame-load", this._onFrameLoad)
    document.removeEventListener("turbo:before-stream-render", this._onBeforeStreamRender)
    document.removeEventListener("turbo:after-stream-render", this._onAfterStreamRender)

    // Очищаем таймеры
    if (this._hideDebounceTimeout) clearTimeout(this._hideDebounceTimeout)
    if (this._maxVisibleTimeout) clearTimeout(this._maxVisibleTimeout)
  }

  beforeCache() {
    // Это самый важный хук: состояние, которое попадёт в snapshot.
    // Делаем так, чтобы при возврате назад snapshot сразу показывал лоадер.
    this.resetOverlayStateForSnapshot()
    this.showLoader()
  }

  onTurboLoad() {
    this.hideLoader()
  }

  onFrameLoad() {
    this.hideLoader()
  }

  onBeforeStreamRender(event) {
    const stream = event?.detail?.newStream
    if (!stream) return

    // Нас интересует только stream, который таргетит текущий элемент компонента.
    // Пример: <turbo-stream action="replace" target="component_332">...
    const action = stream.getAttribute("action")
    const target = stream.getAttribute("target")

    if (!target || target !== this.element.id) return

    // Перед replace/update показываем лоадер.
    if (action === "replace" || action === "update") {
      this.showLoader()
      // Если after-stream не придёт (или придёт поздно/в другой очереди),
      // всё равно попробуем скрыть после небольшой паузы.
      this.scheduleHide()
    }
  }

  onAfterStreamRender(event) {
    const stream = event?.detail?.newStream
    if (!stream) {
      // Если не можем понять stream — пробуем скрыть «пачкой».
      this.scheduleHide()
      return
    }

    const target = stream.getAttribute("target")
    if (target && target !== this.element.id) return

    // После применения stream: скрываем, но debounced,
    // т.к. обычно прилетает несколько stream подряд.
    this.scheduleHide()
  }

  scheduleHide() {
    if (!this.hasOverlayTarget) return

    // Debounce: если прилетело несколько stream подряд — скрываем один раз в конце.
    if (this._hideDebounceTimeout) clearTimeout(this._hideDebounceTimeout)
    this._hideDebounceTimeout = setTimeout(() => {
      this.hideLoader()
    }, 120)
  }

  showLoader() {
    if (!this.hasOverlayTarget) return

    // Аварийный таймаут: не держим лоадер дольше 10 секунд
    if (this._maxVisibleTimeout) clearTimeout(this._maxVisibleTimeout)
    this._maxVisibleTimeout = setTimeout(() => {
      this.hideLoader()
    }, 10000)

    this.overlayTarget.classList.remove("hidden", "opacity-0")
    this.overlayTarget.classList.add("flex")
  }

  hideLoader({ keepTimers = false } = {}) {
    if (!this.hasOverlayTarget) return

    if (!keepTimers) {
      if (this._hideDebounceTimeout) {
        clearTimeout(this._hideDebounceTimeout)
        this._hideDebounceTimeout = null
      }
      if (this._maxVisibleTimeout) {
        clearTimeout(this._maxVisibleTimeout)
        this._maxVisibleTimeout = null
      }
    }

    this.overlayTarget.classList.add("hidden")
    this.overlayTarget.classList.remove("flex", "opacity-0")
  }

  async _loadLazy() {
    try {
      // Пробрасываем текущие URL-параметры страницы (фильтры, drill и т.д.) в lazy-запрос
      const lazyUrl = new URL(this.lazyUrlValue, window.location.origin)
      const pageParams = new URLSearchParams(window.location.search)
      for (const [key, val] of pageParams) {
        if (!lazyUrl.searchParams.has(key)) {
          lazyUrl.searchParams.append(key, val)
        }
      }
      const resp = await fetch(lazyUrl.toString(), {
        headers: { "Accept": "text/vnd.turbo-stream.html" }
      })
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
      const html = await resp.text()
      Turbo.renderStreamMessage(html)
    } catch (e) {
      console.error("[component-loader] lazy load failed:", e)
      // Показываем ошибку в оверлее
      if (this.hasOverlayTarget) {
        this.overlayTarget.innerHTML =
          '<div class="flex flex-col items-center gap-2 p-4">' +
          '<div class="text-xs text-red-500">Ошибка загрузки</div>' +
          '<button class="text-xs text-blue-500 hover:underline" data-action="click->dae20--component-loader#retryLazy">Повторить</button>' +
          '</div>'
        this.overlayTarget.classList.remove("hidden")
        this.overlayTarget.classList.add("flex")
      }
    }
  }

  retryLazy() {
    if (this.hasLazyUrlValue && this.lazyUrlValue) {
      // Восстанавливаем оверлей
      if (this.hasOverlayTarget) {
        this.overlayTarget.innerHTML =
          '<div class="flex flex-col items-center gap-3">' +
          '<div class="loader"></div>' +
          '<span class="text-xs text-gray-500 font-medium">Загрузка...</span>' +
          '</div>'
      }
      this.showLoader()
      this._loadLazy()
    }
  }

  resetOverlayStateForSnapshot() {
    if (!this.hasOverlayTarget) return
    // если у тебя где-то добавлялась прозрачность/анимация — убираем,
    // чтобы snapshot не сохранился “в процессе скрытия”
    this.overlayTarget.classList.remove("opacity-0")
  }
}
